/**
 * Pinia store for the Usage page (T04).
 *
 * Holds paginated per-request log state:
 *   - `rows`       — current page of `UsageRow[]`
 *   - `total`      — server-reported total count across all pages
 *   - paging       — `page`, `pageSize`, derived `totalPages` / `hasPrev` / `hasNext`
 *   - filters      — `from` / `to` (YYYY-MM--DD strings), `errorsOnly`
 *
 * The hub controller's Zod DTO expects full ISO datetime strings for `from`
 * / `to`. This store normalises YYYY-MM-DD (from the URL / date inputs) into:
 *   - `from` → `YYYY-MM-DDT00:00:00.000Z`  (start of day, inclusive)
 *   - `to`   → next-day `YYYY-MM-DDT00:00:00.000Z` (exclusive upper bound)
 *
 * Errors are kept as a single string for the view to surface. The page
 * doesn't need per-call error granularity for the MVP.
 */
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { usageApi, type UsageRow, type UsageSummaryResponse } from '../api/usage';

const DEFAULT_PAGE_SIZE = 50;

export const useUsageStore = defineStore('usage', () => {
  // ── State ────────────────────────────────────────────────────────────────────

  const rows = ref<UsageRow[]>([]);
  const total = ref<number>(0);
  const page = ref<number>(1);
  const pageSize = ref<number>(DEFAULT_PAGE_SIZE);
  /** YYYY-MM-DD string, or null when not set. */
  const from = ref<string | null>(null);
  /** YYYY-MM-DD string, or null when not set. */
  const to = ref<string | null>(null);
  const errorsOnly = ref<boolean>(false);
  const loading = ref<boolean>(false);
  const error = ref<string | null>(null);

  // ── Summary state (Overview page) ─────────────────────────────────────────────

  const summary = ref<UsageSummaryResponse | null>(null);
  const summaryLoading = ref<boolean>(false);
  const summaryError = ref<string | null>(null);

  // ── Computed ─────────────────────────────────────────────────────────────────

  const totalPages = computed<number>(() => {
    if (total.value <= 0) return 1;
    return Math.max(1, Math.ceil(total.value / pageSize.value));
  });

  const hasPrev = computed<boolean>(() => page.value > 1);
  const hasNext = computed<boolean>(() => page.value < totalPages.value);

  // ── Helpers ──────────────────────────────────────────────────────────────────

  /**
   * Convert a YYYY-MM-DD string to an ISO datetime at the start of that day.
   * Returns `undefined` when `dateStr` is null/undefined so it can be omitted
   * from the API call (server applies its default behaviour).
   */
  function toIsoStart(dateStr: string | null | undefined): string | undefined {
    if (!dateStr) return undefined;
    return `${dateStr}T00:00:00.000Z`;
  }

  /**
   * Convert a YYYY-MM-DD string to an ISO datetime at the start of the next
   * day (exclusive upper bound). Returns `undefined` when `dateStr` is null/undefined.
   */
  function toIsoEnd(dateStr: string | null | undefined): string | undefined {
    if (!dateStr) return undefined;
    const d = new Date(`${dateStr}T00:00:00.000Z`);
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString();
  }

  // ── Actions ─────────────────────────────────────────────────────────────────

  /**
   * Fetch the usage list. All parameters are optional; when omitted the store
   * uses its current values.
   */
  async function load(opts?: {
    page?: number;
    page_size?: number;
    from?: string | null;
    to?: string | null;
    errorsOnly?: boolean;
  }): Promise<void> {
    loading.value = true;
    error.value = null;

    const targetPage = opts?.page ?? page.value;
    const targetPageSize = opts?.page_size ?? pageSize.value;
    const targetFrom = opts?.from !== undefined ? opts.from : from.value;
    const targetTo = opts?.to !== undefined ? opts.to : to.value;
    const targetErrorsOnly = opts?.errorsOnly !== undefined ? opts.errorsOnly : errorsOnly.value;

    // Sync state so computed / UI are always in sync with what was actually requested.
    page.value = targetPage;
    pageSize.value = targetPageSize;
    from.value = targetFrom;
    to.value = targetTo;
    errorsOnly.value = targetErrorsOnly;

    try {
      const res = await usageApi.list({
        from: toIsoStart(targetFrom),
        to: toIsoEnd(targetTo),
        errors_only: targetErrorsOnly || undefined,
        page: targetPage,
        page_size: targetPageSize,
      });
      rows.value = res.rows;
      total.value = res.total;
    } catch (err) {
      error.value = (err as Error).message;
    } finally {
      loading.value = false;
    }
  }

  async function nextPage(): Promise<void> {
    if (!hasNext.value) return;
    await load({ page: page.value + 1 });
  }

  async function prevPage(): Promise<void> {
    if (!hasPrev.value) return;
    await load({ page: page.value - 1 });
  }

  /**
   * Merge filter patches, reset to page 1, and reload.
   * Use `null` to explicitly clear a date filter.
   */
  async function setFilters(patch: {
    from?: string | null;
    to?: string | null;
    errorsOnly?: boolean;
  }): Promise<void> {
    await load({
      page: 1,
      from: patch.from,
      to: patch.to,
      errorsOnly: patch.errorsOnly,
    });
  }

  /** Reload the current page with the current filter state. */
  async function refresh(): Promise<void> {
    await load();
  }

  /** Clear all filters and return to page 1. */
  async function reset(): Promise<void> {
    await load({ page: 1, from: null, to: null, errorsOnly: false });
  }

  // ── Summary actions (Overview page) ──────────────────────────────────────────

  /**
   * Fetch KPI summary + welcome bonus totals.
   * The 30-day daily series is always the last 30 days regardless of `since`.
   */
  async function loadSummary(since?: string): Promise<void> {
    summaryLoading.value = true;
    summaryError.value = null;
    try {
      summary.value = await usageApi.summary(since ? { since } : {});
    } catch (err) {
      summaryError.value = (err as Error).message;
    } finally {
      summaryLoading.value = false;
    }
  }

  return {
    rows,
    total,
    page,
    pageSize,
    from,
    to,
    errorsOnly,
    loading,
    error,
    totalPages,
    hasPrev,
    hasNext,
    load,
    nextPage,
    prevPage,
    setFilters,
    refresh,
    reset,
    summary,
    summaryLoading,
    summaryError,
    loadSummary,
  };
});